import {
  DRAW_FIT_FIRST_UNIT_CAMERA_OFFSET_X,
  DRAW_FIT_FIRST_UNIT_CAMERA_Y_OFFSET,
  DRAW_FIT_FIRST_UNIT_CAMERA_Z_DEPTH,
  DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_X,
  DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_Y,
  DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_Z,
  DRAW_FIT_POST_ORBIT_AZIMUTH_DEG,
  DRAW_FIT_POST_ORBIT_POLAR_DEG,
} from '@constants'
import { useUiPresentationStore } from '../../store/uiPresentationStore'
import type { Item, LinkedItem } from '@types'
import type CameraControls from 'camera-controls'
import { MathUtils, PerspectiveCamera } from 'three'

import { captureCameraViewFromControls, isValidCameraView, type CameraView } from './cameraView'
import { calculateItemsBoundingBox } from './fitCameraToItems'

/** En chrome táctil el canvas es más bajo: pan-up para que la pieza no quede arriba del frame. */
const TOUCH_DRAW_FIT_PAN_UP = 420
/** Ligero alejamiento en viewports cortos. */
const TOUCH_DRAW_FIT_EXTRA_DEPTH = 350

function applyPostDrawFitOrbit(controls: CameraControls): void {
  if (DRAW_FIT_POST_ORBIT_AZIMUTH_DEG === 0 && DRAW_FIT_POST_ORBIT_POLAR_DEG === 0) {
    return
  }
  void controls.rotate(
    MathUtils.degToRad(DRAW_FIT_POST_ORBIT_AZIMUTH_DEG),
    MathUtils.degToRad(DRAW_FIT_POST_ORBIT_POLAR_DEG),
    false,
  )
  controls.update(0)
}

function countRegularUnits(items: Map<string, Item | LinkedItem>): number {
  let count = 0
  for (const item of items.values()) {
    if (
      (item.type === 'items' || item.type === 'linkedItem') &&
      item.matrix &&
      item.width &&
      item.height
    ) {
      count += 1
    }
  }
  return count
}

/**
 * Primera unidad: encuadre canónico (posición/target de producto).
 * Más unidades: no mueve la cámara; preserva el punto de vista actual del usuario.
 */
export function applyDrawCameraFit(
  controls: CameraControls,
  camera: PerspectiveCamera,
  items: Map<string, Item | LinkedItem>,
): CameraView | null {
  const regularUnitCount = countRegularUnits(items)
  // 2ª unidad en adelante: conservar el mismo punto de vista (sin re-centrar ni dolly).
  if (regularUnitCount !== 1) return null

  const boundingBoxData = calculateItemsBoundingBox(items)
  if (!boundingBoxData) return null

  const { center } = boundingBoxData
  const isTouchChrome = useUiPresentationStore.getState().layout !== 'desktop'
  const panUp = isTouchChrome ? TOUCH_DRAW_FIT_PAN_UP : 0
  const extraDepth = isTouchChrome ? TOUCH_DRAW_FIT_EXTRA_DEPTH : 0

  controls.stop()

  const cameraPosition = {
    x: center.x + DRAW_FIT_FIRST_UNIT_CAMERA_OFFSET_X,
    y: center.y + DRAW_FIT_FIRST_UNIT_CAMERA_Y_OFFSET + panUp,
    z: center.z - DRAW_FIT_FIRST_UNIT_CAMERA_Z_DEPTH - extraDepth,
  }
  const targetPosition = {
    x: center.x + DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_X,
    y: center.y + DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_Y + panUp,
    z: center.z + DRAW_FIT_FIRST_UNIT_TARGET_OFFSET_Z,
  }

  // `false` = sin transición: evita salto al dibujar la primera unidad.
  controls.setTarget(targetPosition.x, targetPosition.y, targetPosition.z, false)
  controls.setPosition(cameraPosition.x, cameraPosition.y, cameraPosition.z, false)
  camera.updateMatrixWorld()
  controls.update(0)
  applyPostDrawFitOrbit(controls)

  const fittedView = captureCameraViewFromControls(controls, camera)
  return isValidCameraView(fittedView) ? fittedView : null
}
